import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useSettings } from '@wu/widget-core';
import { meta } from '../../../../meta';
import {
  getGutter,
  getImagesByQuality,
  getLayoutObjFromName,
  masonryLayoutMode,
  SIZE_TYPE,
  SIZES
} from '../../../helpers';
import { useResizeWidth } from '../../../utils';
import { ItemGrid } from '../../ItemGrid';
import { useImagesAspectRatios } from '../../useImagesAspectRatios';
import { Boxes } from './Boxes';
import { MasonryItem } from './MasonryItem';

export const Masonry = ({ mobile, tablet, images, reportVisibleImages }) => {
  const masonryRef = useRef(null);
  const settings = useSettings();
  const smallImages = getImagesByQuality(images, SIZE_TYPE.SMALL);
  const mediumImages = getImagesByQuality(images, SIZE_TYPE.MEDIUM);
  const ratios = useImagesAspectRatios(mediumImages);
  const { layout } = settings;
  const { name } = getLayoutObjFromName(layout);
  const { measureRef, width } = useResizeWidth();

  const [layoutMode, setLayoutMode] = useState(masonryLayoutMode(layout));

  const layoutRearrangeTimeout = useRef(null);

  useEffect(() => {
    reportVisibleImages(images.map(({ id }) => id));
  }, [images, reportVisibleImages]);

  const rearrangeLayout = () => {
    if (masonryRef.current) {
      const { isotope } = masonryRef.current.state;

      isotope.layout();
      isotope.reloadItems();
      isotope.arrange();
    }
  };

  useLayoutEffect(() => {
    clearTimeout(layoutRearrangeTimeout.current);

    layoutRearrangeTimeout.current = setTimeout(() => {
      setTimeout(rearrangeLayout); // ¯\_(ツ)_/¯
      setTimeout(rearrangeLayout, 400); // ¯\_(ツ)_/¯
      setTimeout(rearrangeLayout, 1000); // ¯\_(ツ)_/¯
      setTimeout(rearrangeLayout, 2000); // ¯\_(ツ)_/¯
    }, 500);
  });

  useLayoutEffect(() => {
    setLayoutMode(masonryLayoutMode(layout));
  }, [layout]);

  const gutter = (() => {
    const {
      default: defaultGutter,
      mobile: mobileGutter,
      tablet: tabletGutter
    } = getGutter(settings);

    if (mobile) {
      return mobileGutter;
    }

    if (tablet) {
      return tabletGutter;
    }

    return defaultGutter;
  })();

  return (
    <Container ref={measureRef}>
      <Boxes
        ratios={ratios}
        width={width}
        mobile={mobile}
        tablet={tablet}
        gutter={gutter}
        settings={settings}
      >
        {({ sizes }) => {
          const showItem = !!sizes?.boxes.length;

          return (
            <ItemGrid
              key={name}
              ref={masonryRef}
              options={{
                itemSelector: `.${meta.stylePrefix}-masonry-item-item`,
                layoutMode,
                packery: {
                  gutter: 0
                },
                [layoutMode]: {
                  gutter
                }
              }}
            >
              {showItem &&
                sizes.boxes.map((item, index) => {
                  const { width, height } = item || {};
                  if (!width || !height) {
                    return null;
                  }
                  const currentImages =
                    width > SIZES[SIZE_TYPE.SMALL] ? mediumImages : smallImages;
                  const imageData = currentImages[index];

                  if (!imageData) {
                    return null;
                  }

                  return (
                    <MasonryItem
                      key={imageData.id}
                      id={index}
                      item={item}
                      data={imageData}
                      gutter={gutter}
                    />
                  );
                })}
            </ItemGrid>
          );
        }}
      </Boxes>
    </Container>
  );
};

const Container = styled.div.attrs({
  className: `${meta.stylePrefix}-masonry-component`
})`
  opacity: 1;
  overflow: hidden;
  max-width: 100%;
`;
